/**
 * Makes a native date/time input picker-only: the value can be set from the
 * browser's own picker, but never typed or pasted in.
 *
 * Typing into `<input type="date">` is what lets an operator land a value the
 * field's own min/max never sees — a stray keystroke on the year segment turns
 * 2026 into 0226, and pasting bypasses the segments entirely. Blocking both
 * leaves the picker as the only way in, so the value is always one the picker
 * was willing to offer.
 *
 * Tab is deliberately still allowed so the field does not trap keyboard focus.
 * The trade-off is that the arrow-key/number entry a keyboard-only user would
 * otherwise rely on goes away with it; the picker itself remains reachable with
 * Enter or Space on the calendar indicator.
 *
 * Spread onto the input, after any handlers of its own:
 *
 *   <input type="date" value={x} onChange={...} {...pickerOnly()} />
 *   <input type="date" ... {...pickerOnly({ disabled: !ticket })} />
 */
export function pickerOnly({ disabled = false } = {}) {
  return {
    onKeyDown: (e) => { if (e.key !== 'Tab') e.preventDefault(); },
    onPaste: (e) => e.preventDefault(),
    // Clicking anywhere in the field opens the picker, not just the small native
    // indicator — otherwise a field you cannot type into looks inert. showPicker
    // throws without user activation and is missing on older browsers, where the
    // native indicator still works on its own.
    onClick: (e) => {
      if (disabled) return;
      try {
        e.currentTarget.showPicker?.();
      } catch {
        /* fall back to the browser's own indicator */
      }
    },
  };
}
